"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, ExternalLink } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { OpenSignupButton } from "@/components/popups/OpenSignupButton";
import { MobileMenu } from "@/components/layout/MobileMenu";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { assetUrl } from "@/lib/asset-url";
import { getPrimaryNav } from "@/lib/site-menu";
import { localizedHref, useLocale } from "@/i18n/locale";

// ids das seções da home que têm um link correspondente na navegação
// principal — precisa ficar em sincronia com src/content/nav.ts (primaryNav)
// e os id="..." de cada <section>, senão o scroll-spy simplesmente ignora a
// seção nova e o item correspondente no menu nunca acende.
const SECTION_IDS = ["clientes", "solucoes", "como-funciona", "integracoes", "faq"];

export function Header() {
  const pathname = usePathname();
  const locale = useLocale();
  const primaryNav = getPrimaryNav().map((link) => ({
    ...link,
    href: localizedHref(link.href, locale),
  }));
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sectionActive, setSectionActive] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const isHome = pathname === "/" || pathname === "/en" || pathname === "/en/";
    if (!isHome) {
      return;
    }

    let frame = 0;
    const updateActiveSection = () => {
      frame = 0;
      const activationLine = 112;
      const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(
        (section): section is HTMLElement => section !== null,
      ).sort((a, b) => a.offsetTop - b.offsetTop);
      const passedSections = sections.filter(
        (section) => section.getBoundingClientRect().top <= activationLine,
      );
      setSectionActive(passedSections.at(-1)?.id ?? null);
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    const domObserver = new MutationObserver(scheduleUpdate);
    domObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      domObserver.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  // No WordPress, os links vêm do menu real do wp-admin (via
  // window.SATLABS_CONFIG.menu) — o href de cada item pode estar como
  // âncora relativa ("#solucoes", cadastro manual) ou URL absoluta
  // ("http://site/#clientes", inserida pelo self-heal do menu em
  // functions.php). Nenhum dos dois bate com o "/#id" fixo que o Next.js
  // usa, então comparar a string inteira nunca ativa nada no WordPress —
  // extrair só o fragmento (depois do "#") resolve os dois casos.
  const hrefFragment = (href: string) => href.split("#")[1] ?? null;

  const isHomePath = pathname === "/" || pathname === "/en" || pathname === "/en/";
  const activeHref =
    isHomePath
      ? sectionActive
      : (primaryNav.find((link) => pathname === link.href || pathname.startsWith(`${link.href}/`))
          ?.href ?? null);

  return (
    // Fragment em vez de aninhar o MobileMenu dentro do <header>: um header
    // com position:sticky vira containing block dos filhos position:fixed
    // nos navegadores atuais, o que espremia o menu mobile na altura do
    // header (72px) em vez de ocupar a tela inteira.
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
          scrolled ? "border-b border-line bg-bg-base/90 backdrop-blur" : "border-b border-transparent bg-transparent"
        }`}
      >
        <Container>
          <div className="flex h-18 items-center justify-between py-4">
            <Link href="/" className="shrink-0" aria-label="SAT Labs">
              <Image src={assetUrl("/brand/sat-labs-logo-white.png")} alt="SAT Labs" width={600} height={195} priority className="h-7 w-auto" />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {primaryNav.map((link) => {
                const isActive = isHomePath
                  ? hrefFragment(link.href) === activeHref
                  : link.href === activeHref;

                if (link.highlight) {
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      aria-current={isActive ? "page" : undefined}
                      className="ml-1 flex items-center gap-2 rounded-full border border-accent-lime/50 bg-accent-lime/10 px-4 py-1.5 text-sm font-medium text-accent-lime transition-colors hover:bg-accent-lime/20"
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-lime opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-lime" />
                      </span>
                      {link.label}
                    </Link>
                  );
                }

                if (link.external) {
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 text-sm text-text-secondary transition-colors hover:text-text-primary"
                    >
                      {link.label}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  );
                }

                return (
                  <div key={link.href} className="relative">
                    <Link
                      href={link.href}
                      aria-current={isActive ? "location" : undefined}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm transition-colors ${
                        isActive ? "text-text-primary" : "text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="nav-active-dot"
                          className="relative flex h-1.5 w-1.5"
                          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        >
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-lime opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-lime" />
                        </motion.span>
                      )}
                      {link.label}
                    </Link>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-underline"
                        className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-accent-lime"
                        style={{ boxShadow: "0 0 8px 1px rgba(153,255,0,0.7)" }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      />
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="hidden items-center gap-3 lg:flex">
              <LanguageSwitcher />
              <div className="hidden xl:block">
                <OpenSignupButton>Solicitar diagnóstico</OpenSignupButton>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
              className="rounded-md p-2 text-text-primary lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </Container>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
